import React, { useState, useMemo, useRef } from 'react';
import { Form } from '@bedrock/components';
import { useControllableValue, useDebounceEffect, useMemoizedFn, useMount, useUpdateLayoutEffect } from 'ahooks';
import { isEqual, get, set } from 'lodash';
import { FormItemKeyType } from '#components/SForm/SFormItem/index';
import { formItemKeyToList } from '#components/SForm/SFormItem/utils';

type FormItemValueTransformProps = {
  /**
   * 把 children 受控 value 的字段映射到外层 form 的字段路径。
   *
   * keyMap 的 key 是 children value 上的 lodash path，决定了 children value
   * 的整体形态：
   *   { '[0]': ..., '[1]': ... }       → value 是数组 [v0, v1]
   *   { user_id: ..., user_name: ... } → value 是对象 { user_id, user_name }
   *   { 'a[0]': ..., 'a[1]': ... }     → value 是 { a: [v0, v1] }
   *   { 'a.b': ..., 'a.c': ... }       → value 是 { a: { b, c } }
   *   { 'a[0].x': ..., 'a[1].x': ... } → value 是 { a: [{ x }, { x }] }
   *
   * 起点判断：keyMap 的 keys 全部以 '[' 开头 → 数组 []，否则 → 对象 {}。
   * 嵌套结构由 lodash set 在 path token 上自动决定中间节点类型。
   *
   * keyMap 的 value 是外层 form 的字段路径（由 formItemKeyToList 解析）。
   *
   * 注意：当 keyMap 已声明数组形态（如 `{'[0]':..., '[1]':...}`），children
   * 组件不再需要靠 `defaultValue: []` 来兜底形态——形态已由 keyMap 直接告诉
   * 本组件，无论 children 初始 value 是 undefined 还是空数组都能正确重建。
   */
  keyMap?: Record<string, FormItemKeyType>;
} & React.ComponentProps<typeof Form.Item>;

function isUndefined(value: any) {
  return typeof value === 'undefined';
}

/**
 * 判断 wrapper 累加结果是否仍处于"未初始化"状态：null/undefined 或没有任何
 * key 的对象。用于避免把"还没采集到任何子项"的空 wrapper 与 children 真实
 * value 互相覆盖（修复默认 value=undefined 时被强行变成 `{}` 的历史 bug）。
 */
function isEmptyWrapper(wrapper: any): boolean {
  if (!wrapper) return true;
  if (typeof wrapper === 'object' && !Array.isArray(wrapper)) {
    return Object.keys(wrapper).length === 0;
  }
  return false;
}

/**
 * 根据 keyMap 的 keys 推断 children value 顶层起点：
 * - 全部以 '[' 开头 → 数组起点 []
 * - 否则           → 对象起点 {}
 *
 * 嵌套结构（如 'a[0]'、'a.b'）由 lodash set 在重建过程中自动按 path token
 * 是数字还是字符串决定中间节点类型。
 */
function inferTopLevelInit(keyMapKeys: string[]): any {
  if (keyMapKeys.length === 0) return {};
  return keyMapKeys.every((k) => k.startsWith('[')) ? [] : {};
}

type ChildItemProps = {
  value?: any;
  onChange?: (value: any) => void;

  valueKey: string;
  parentValue: any;
  parentOnChange: (value: any) => void;
};

// 虚拟子组件，用于传递 value 和 onChange，同步给 FormItem
function VirtualChildItem({ parentValue, valueKey, parentOnChange, ...props }: ChildItemProps) {
  const [value, setValue] = useControllableValue(props);
  const [localValue, setLocalValue] = useState(value);

  // 直接用字面 key 读 wrapper：避免 valueKey='a[0]' 被 lodash get 误解析为
  // "对象 a 的下标 0"。wrapper 的 key 与 keyMap 的 key 一一对应，是字面字符串。
  const parentShowValue = parentValue?.[valueKey] ?? null;

  const changeValue = useMemoizedFn(() => {
    if (isUndefined(parentShowValue) && isUndefined(value)) return;
    if (!isEqual(value, localValue)) {
      // value 外部变更，赋值并改变外部 parent
      setLocalValue(value);
      parentOnChange({
        [valueKey]: value,
      });
    } else if (!isEqual(parentShowValue, value)) {
      // parent 变更，赋值并改变 local
      setValue(parentShowValue);
      setLocalValue(parentShowValue);
    }
  });

  useMount(() => {
    // 初始化时，如果内部有值，则以内部为准，向 parent 同步
    if (!isEqual(parentShowValue, value) && !isUndefined(value)) {
      parentOnChange({
        [valueKey]: value,
      });
    }
  });

  useUpdateLayoutEffect(() => {
    changeValue();
  }, [value, localValue, parentValue?.[valueKey]]);
  return <></>;
}

/**
 * 把扁平 wrapper 还原成 children 期望的真实 value 形态。
 *
 * 转换基于 keyMapKeys 列表，每个 key 都是 children value 上的 lodash path。
 * 使用 lodash set 在空起点上重建：
 *   wrapper={ '[0]':x, '[1]':y },   keys=['[0]','[1]']      → [x, y]
 *   wrapper={ 'a[0]':x, 'a[1]':y }, keys=['a[0]','a[1]']    → { a: [x, y] }
 *   wrapper={ user_id:x, name:y },  keys=['user_id','name'] → { user_id: x, name: y }
 *
 * 关键设计：
 * - 对 keyMapKeys 中的每个 path 都调用 set，包括值为 undefined 的 path。
 *   这保证了重建出的结构形状完整——不会因为某个 path 还没赋值就丢失中间
 *   数组/对象节点（否则 {rows:[{k:'abc'}]} 会变成 length=1 的数组而非
 *   {rows:[{k:'abc'},{k:undefined}]}，后续比较逻辑会持续用残缺结构覆写
 *   children value）。
 * - result 从空起点 ([] 或 {}) 重建，set 在路径上自动创建中间节点；
 *   叶子节点直接复用 wrapper / refValue 的引用，不会破坏 Dayjs / decimal.js
 *   等类实例（故意不引入 cloneDeep）。
 * - refValue 仅作为"wrapper 中该 path 没值时"的兜底来源；refValue 中位于
 *   keyMap 之外的字段不会保留——这是 formItemKeyMap "完整声明 children
 *   value 形态"契约的自然结果。
 */
function wrapperToValue(
  wrapperObj: Record<string, any> | null | undefined,
  refValue: any,
  keyMapKeys: string[],
): any {
  const result: any = inferTopLevelInit(keyMapKeys);
  keyMapKeys.forEach((path) => {
    let v = wrapperObj?.[path];
    if (isUndefined(v)) v = get(refValue, path);
    // 即使 v 仍是 undefined 也必须 set —— 让 lodash 创建中间节点，保持结构完整
    set(result, path, v);
  });
  return result;
}

/**
 * 把 children 真实 value 按 keyMapKeys 投影成扁平 wrapper。
 *
 * 形态敏感策略：
 * - isArrayShape=true（keyMap keys 全以 '[' 开头）：全量提取，包括 undefined。
 *   数组缩短/清空时旧下标 path 已不在 keyMapKeys 中，本函数不处理；残留
 *   由 replaceWrapperValue 全量替换旧 wrapper 完成。
 * - isArrayShape=false（命名 key / 嵌套 path）：跳过 undefined 值，避免
 *   单字段变更时把其他未设置字段覆盖为 undefined/null。
 *
 * TODO: 当业务需要严格区分 null vs undefined 时（例如必填校验对两者行为
 *   不一致），需要让 VirtualChildItem 能把 form 字段写为 undefined，或显式
 *   调用 form.setFieldValue(path, undefined) 主动清理。
 */
function valueToWrapper(val: any, keyMapKeys: string[], isArrayShape: boolean): Record<string, any> {
  const wrapper: Record<string, any> = {};
  // 当 val 本身为 null/undefined（整个字段被清空）时，强制将所有 key 映射为
  // undefined，防止非数组形态下"跳过 undefined"导致 wrapper 不清空、进而触发
  // wrapperToValue 旧值回灌（用户清空后值又闪回）。
  const isValCleared = val === null || val === undefined;
  // 防御 selectFormat.optionsToValue 将 undefined 清空值"包装"为对象（如
  // {id: undefined, name: undefined}）导致的伪清空问题：此时 type 是 object 但
  // 所有映射 key 全为 undefined，本质与直接 clear 相同，必须整体清空 wrapper。
  const isValAllUndefined =
    !isValCleared && typeof val === 'object' && keyMapKeys.length > 0
    && keyMapKeys.every((path) => isUndefined(get(val, path)));
  const shouldClear = isValCleared || isValAllUndefined;
  keyMapKeys.forEach((path) => {
    const v = shouldClear ? undefined : get(val, path);
    // 非数组形态：跳过 undefined，避免将未设置的嵌套字段覆盖为 null。
    // 例外：如果 val 本身已被清空（shouldClear），则必须传出 undefined 以清空对应
    // Form.Item 字段。
    if (!isArrayShape && isUndefined(v) && !shouldClear) return;
    wrapper[path] = v;
  });
  return wrapper;
}

// 包裹子组件，用于传递 value 和 onChange
type WrapperChildrenProps = {
  wrapperValue: any;
  wrapperOnChange: (value: any) => void;
  /**
   * 数组形态下用"全量替换"代替"浅合并"，避免缩短/清空时旧下标残留。
   * 非数组形态仍走 wrapperOnChange 的合并语义。
   */
  wrapperOnReplace: (value: Record<string, any>) => void;
  /** keyMap 的 keys 列表，作为 wrapperToValue / valueToWrapper 的 path 权威 */
  keyMapKeys: string[];
  /** keyMap keys 是否全部以 '[' 开头（数组形态） */
  isArrayShape: boolean;
  value?: any;
  onChange?: (value: any) => void;
  children: React.ReactElement;
};

function WrapperChildren({
  children,
  wrapperValue,
  wrapperOnChange,
  wrapperOnReplace,
  keyMapKeys,
  isArrayShape,
  ...props
}: WrapperChildrenProps): React.ReactElement {
  // 用于重写 value 和 onChange
  const [value, setValue] = useControllableValue(props);
  const [localValue, setLocalValue] = useState(value);

  // 跟踪 value 是否曾被初始化为真实对象/数组（区别于 undefined/null 的"未设置"态），
  // 用于区分"从未设置过"（需从 wrapper 重建）和"用户主动清空"（不应重建）两种场景。
  const valueWasInitializedRef = useRef(false);

  const changeValue = useMemoizedFn(() => {
    // 追踪 value 是否曾被设置为真实对象（用户已选择过值）
    if (value !== null && typeof value === 'object') {
      valueWasInitializedRef.current = true;
    }

    // Fix 1: 空 wrapper + 未初始化 value 视为完全空态，跳过避免互相覆盖
    if (isEmptyWrapper(wrapperValue) && isUndefined(value)) return;
    // 如果 value 还未初始化为对象/数组、且 wrapper 已经有数据，则按 keyMap
    // 形态从 wrapper 重建出 children 期望的 value 进行回灌。
    // ⚠️ 前提：valueWasInitializedRef 为 false（从未设置过），防止用户主动清空后
    // 被错误地当作"未初始化"而重建旧值（导致删除后闪回的问题）。
    if (typeof value !== 'object' && !isEmptyWrapper(wrapperValue) && !valueWasInitializedRef.current) {
      const tempValue = wrapperToValue(wrapperValue, value, keyMapKeys);
      setValue(tempValue);
      setLocalValue(tempValue);
      return;
    }

    if (!isEqual(value, localValue)) {
      // value 外部变更：按 keyMapKeys 投影成扁平 wrapper。
      setLocalValue(value);
      const vw = valueToWrapper(value, keyMapKeys, isArrayShape);
      if (isArrayShape) {
        // 数组形态：全量替换，避免缩短/清空时旧下标残留
        wrapperOnReplace?.(vw);
      } else {
        wrapperOnChange?.(vw);
      }
    } else if (!isEqual(wrapperToValue(wrapperValue, value, keyMapKeys), value)) {
      // parent 变更，赋值并改变 local
      const tempValue = wrapperToValue(wrapperValue, value, keyMapKeys);
      setValue(tempValue);
      setLocalValue(tempValue);
    }
  });

  useDebounceEffect(
    () => {
      changeValue();
    },
    [value, localValue, wrapperValue],
    {
      wait: 40,
      leading: false,
      trailing: true,
    },
  );

  // children 已通过类型收紧到 ReactElement；运行时仍用 Children.only 兜底防御
  const onlyChild = React.Children.only(children);
  return React.cloneElement(onlyChild, {
    value,
    onChange: setValue,
  });
}

export default function FormItemValueTransform({ keyMap, ...props }: FormItemValueTransformProps) {
  const [wrapperValue, setWrapperValue] = useState<Record<string, any>>({});

  const keyMapKeys = useMemo(() => (keyMap ? Object.keys(keyMap) : []), [keyMap]);

  const isArrayShape = useMemo(() => inferTopLevelInit(keyMapKeys) instanceof Array, [keyMapKeys]);

  const updateWrapperValue = useMemoizedFn((value: any) => {
    if (isUndefined(value)) return;
    setWrapperValue((oldValue) => {
      return {
        ...oldValue,
        ...value,
      };
    });
  });

  // 数组形态需要完整替换 wrapperValue（不能再做合并），
  // 否则缩短/清空时旧下标永远残留。
  const replaceWrapperValue = useMemoizedFn((next: Record<string, any>) => {
    setWrapperValue(next || {});
  });

  const formItems = useMemo(() => {
    if (!keyMap || !wrapperValue) {
      return null;
    }
    return Object.keys(keyMap).map((key) => {
      const formItemKey = keyMap[key];
      return (
        <Form.Item
          key={key}
          name={formItemKeyToList(formItemKey)}
          style={{ display: 'none' }}
        >
          <VirtualChildItem
            valueKey={key}
            parentValue={wrapperValue}
            parentOnChange={updateWrapperValue}
          />
        </Form.Item>
      );
    });
  }, [keyMap, wrapperValue]);

  return (
    <>
      <Form.Item {...props}>
        <WrapperChildren
          wrapperValue={wrapperValue}
          wrapperOnChange={updateWrapperValue}
          wrapperOnReplace={replaceWrapperValue}
          keyMapKeys={keyMapKeys}
          isArrayShape={isArrayShape}
        >
          {React.Children.only(props.children) as React.ReactElement}
        </WrapperChildren>
      </Form.Item>
      {formItems}
    </>
  );
}
